import axios from "axios";
import { dropEmptyKeys } from "../core";
import { isSessionExpired, refreshCsrfToken } from "../csrf-token-manager";
import getMetaEnv from "../meta";
import { asyncRetryIfTransientError, fetchWithRetry } from "../retries";
import isSameOriginRequest from "../same-origin";
import { withCache } from "./cache";
import buildRequestFingerprint from "./build-request-fingerprint";
import { isAxiosNetworkError, shouldReportError } from "./error-classification";
import { buildNetworkConnectionError, reportRequestError } from "./error-reporting";
import { isRetriableGetError, SINGLE_FETCH_ATTEMPT } from "./get-retry-policy";

import { isConnectResetError, withConnectResetRetry } from "./connect-reset-retry";

export { isAxiosNetworkError, shouldReportError, buildRequestFingerprint, isConnectResetError };

const withCsrfRetry = async (requestFn, source) => {
  try {
    return await requestFn();
  } catch (error) {
    if (!isSessionExpired(error)) throw error;

    const refreshed = await refreshCsrfToken(source);
    if (!refreshed) {
      throw error;
    }

    return requestFn();
  }
};

// MN-9405: CSRF retry (session-expiry) stays the inner layer; wrap it in a single connect-reset 503
// retry. Opt-in (`!skip`) so the documented double-submit edge case can never reach payment/checkout
// mutations — only the cart-add endpoints that accepted it pass `retryConnectReset` (api.js
// retryablePost). `noRetry` also skips.
const runMutation = (execute, skip) =>
  withConnectResetRetry(() => withCsrfRetry(execute, "request_layer"), skip);

const AXIOS_DEFAULT_TIMEOUT = 15_000;

const MUTATION_METHODS = ["POST", "PUT", "PATCH", "DELETE"];

const defaultHeaders = () => ({
  "X-CSRF-Token": getMetaEnv("csrf-token"),
  "X-Requested-With": "XMLHttpRequest",
  Accept: "application/json",
  "Content-Type": "application/json; charset=utf-8"
});

const buildParams = ({ headers = {}, ...rest }, method) => ({
  method,
  headers: dropEmptyKeys({
    ...defaultHeaders(),
    ...headers
  }),
  credentials: "same-origin",
  ...rest
});

const haltErrorResponse = async response => {
  if (response.status >= 400 && response.status <= 599) {
    let errorMessage;
    let responseData;
    let textBody;
    try {
      textBody = await response.text();
    } catch (readError) {
      // MN-8956: a dropped connection while reading a gateway (5xx) body rejects
      // response.text() with a status-less TypeError; re-attach the status so the
      // unified GET retry still sees it as transient (not 0 retries / net sentinel).
      readError.status = response.status;
      throw readError;
    }

    try {
      responseData = JSON.parse(textBody);
    } catch {
      responseData = textBody;
    }

    if (textBody) {
      errorMessage =
        response.status === 422
          ? textBody
          : `${response.statusText || `HTTP ${response.status}`}: ${textBody}`;
    } else {
      errorMessage = response.statusText || `HTTP ${response.status}`;
    }
    const error = new Error(errorMessage);
    error.status = response.status;
    error.message = errorMessage;
    error.response = responseData; // Preserve the parsed response data
    throw error;
  }
  return response;
};

const increaseAsyncRequestsCount = url => {
  if (isSameOriginRequest(url)) window.ajax_active += 1;
};

const decreaseAsyncRequestsCount = url => {
  if (isSameOriginRequest(url)) window.ajax_active -= 1;
};

const sendBeacon = (url, data) =>
  new Promise((resolve, reject) => {
    const dataString = JSON.stringify(data);
    const queued = navigator.sendBeacon(url, dataString);

    if (queued) {
      resolve();
    } else {
      reject(new Error(`Failed to queue data for sending to ${url}`));
    }
  });

const toJson = async (response, url, method) => {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch (parseError) {
    const error = new Error(`Non-JSON response for ${method} ${url}: ${text.slice(0, 80)}`);
    error.status = response.status;
    error.response = text;
    error.cause = parseError;
    throw error;
  }
};

const runFetchPipeline = (fetchPromise, url, method) =>
  fetchPromise.then(haltErrorResponse).then(response => toJson(response, url, method));

const executeFetch = (url, params, method) =>
  runFetchPipeline(fetchWithRetry(url, buildParams(params, method)), url, method);

// MN-8956: retrying GETs use one policy (transient gateway 5xx OR network) under a
// single 3-attempt budget; the inner fetchWithRetry is collapsed to one attempt (see
// get-retry-policy.js). retryDelay: 500 keeps the old fetch backoff (~1s, ~2s vs ~2s, ~4s).
const executeGetWithRetry = (url, params, method) =>
  asyncRetryIfTransientError(
    () =>
      runFetchPipeline(
        fetchWithRetry(url, buildParams(params, method), SINGLE_FETCH_ATTEMPT),
        url,
        method
      ),
    { errorMatch: isRetriableGetError, remainingTries: 2, retryDelay: 500 }
  );

// `silent: true` skips notifyError() for known-noisy probes/polls (error still
// rethrown so caller .catch handlers run; only the Honeybadger notify pipe is
// suppressed — see HB-1851). `noRetry: true` skips the transient GET retry.
const request = (url, params = {}, method = "GET") => {
  const { silent, noRetry, retryConnectReset, ...rest } = params;
  increaseAsyncRequestsCount(url);

  if (rest.sendBeacon) {
    delete rest.sendBeacon;
    decreaseAsyncRequestsCount(url);
    return sendBeacon(url, rest);
  }

  const upperMethod = method.toUpperCase();
  const execute = () => executeFetch(url, rest, method);

  const runFetch = () =>
    upperMethod === "GET" && !noRetry ? executeGetWithRetry(url, rest, method) : execute();

  const fetchPromise = MUTATION_METHODS.includes(upperMethod)
    ? runMutation(execute, !retryConnectReset || noRetry)
    : runFetch();

  return fetchPromise
    .catch(error => {
      reportRequestError(error, { url, method, responseBody: error.response, silent });
      // Only convert to the network sentinel when there is no HTTP status (mirrors
      // shouldReportError's guard so server error bodies are not swallowed).
      const httpStatus = error.status ?? error.response?.status;
      if (!httpStatus && isAxiosNetworkError(error)) {
        throw buildNetworkConnectionError(error);
      }
      throw error;
    })
    .finally(() => decreaseAsyncRequestsCount(url));
};

// Non-native (axios) request. Prefer the default export unless you need axios-only
// features like upload progress tracking.
const executeAxios = (url, params, method) => {
  const { timeout, ...axiosParams } = buildParams(params, method);
  return axios(url, {
    ...axiosParams,
    timeout: timeout ?? AXIOS_DEFAULT_TIMEOUT
  })
    .then(haltErrorResponse)
    .then(response => response.data);
};

export const axiosRequest = (url, params = {}, method = "GET") => {
  increaseAsyncRequestsCount(url);
  const { noRetry, silent, ...requestParams } = params;

  if (requestParams.sendBeacon) {
    delete requestParams.sendBeacon;
    decreaseAsyncRequestsCount(url);
    return sendBeacon(url, requestParams);
  }

  const upperMethod = method.toUpperCase();
  const isGet = upperMethod === "GET";
  const execute = () =>
    MUTATION_METHODS.includes(upperMethod)
      ? withCsrfRetry(() => executeAxios(url, requestParams, method), "axios_request_layer")
      : executeAxios(url, requestParams, method);

  // MN-8956: axios GETs share the unified predicate, so they now retry transient
  // gateway 5xx too (parity with fetch); no inner layer here, so it cannot multiply.
  const shouldRetry = isGet && !noRetry;
  const axiosPromise = shouldRetry
    ? asyncRetryIfTransientError(execute, { errorMatch: isRetriableGetError })
    : execute();

  return axiosPromise
    .catch(error => {
      reportRequestError(error, { url, method, responseBody: error.response?.data, silent });
      throw error;
    })
    .finally(() => decreaseAsyncRequestsCount(url));
};

export default withCache(request);
